/**
 * PATH: packages/ui/src/kanban/components/lane/inbox-filters/useInboxLaneFilters.ts
 * FILE_ID: UI-KANBAN-INBOX-LANE-FILTER-HOOK-001
 * PURPOSE: Persist + apply curated primary-inbox lane filters; fetch category allow-list.
 * WHY: Keeps filter state out of Email Hub URL/rail keys and off the lane frame.
 * MAIN EXPORTS: useInboxLaneFilters
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { Card } from '../../../types'
import type { KanbanEmailAdapter, KanbanEmailRailFilter } from '../../../api'
import {
  EMPTY_INBOX_LANE_FILTERS,
  applyInboxLaneFilters,
  inboxLaneFilterQuery,
  loadInboxLaneFilters,
  saveInboxLaneFilters,
  type InboxLaneFilterState,
} from './model'

export interface UseInboxLaneFiltersArgs {
  enabled: boolean
  boardId: string | undefined
  cards: Card[]
  unseenByCard?: Record<string, number>
  listInboxEmails?: KanbanEmailAdapter['listInboxEmails']
  buildInboxListUrl?: KanbanEmailAdapter['buildInboxListUrl']
}

export function useInboxLaneFilters({
  enabled,
  boardId,
  cards,
  unseenByCard,
  listInboxEmails,
  buildInboxListUrl,
}: UseInboxLaneFiltersArgs) {
  const [filters, setFilters] = useState<InboxLaneFilterState>(() =>
    boardId ? loadInboxLaneFilters(boardId) : { ...EMPTY_INBOX_LANE_FILTERS },
  )

  useEffect(() => {
    if (!enabled || !boardId) return
    setFilters(loadInboxLaneFilters(boardId))
  }, [enabled, boardId])

  const onChange = useCallback(
    (next: InboxLaneFilterState) => {
      setFilters(next)
      if (boardId) saveInboxLaneFilters(boardId, next)
    },
    [boardId],
  )

  const onClear = useCallback(() => {
    onChange({ ...EMPTY_INBOX_LANE_FILTERS })
  }, [onChange])

  const categoryFilter: KanbanEmailRailFilter | null = useMemo(() => {
    if (!enabled || !filters.category || !listInboxEmails) return null
    const filter: KanbanEmailRailFilter = {
      category: filters.category,
      limit: 200,
    }
    if (filters.isRead === 'unread') filter.isRead = false
    if (filters.isRead === 'read') filter.isRead = true
    if (filters.allocation === 'allocated' || filters.allocation === 'unallocated') {
      filter.allocation = filters.allocation
    }
    const mailbox = filters.mailbox.trim()
    if (mailbox) {
      const n = Number(mailbox)
      if (Number.isFinite(n) && n > 0 && String(n) === mailbox) {
        filter.accountId = n
      }
    }
    if (filters.search.trim()) filter.search = filters.search.trim()
    return filter
  }, [enabled, filters, listInboxEmails])

  const categoryQ = useQuery({
    queryKey: ['kanban', 'inbox-lane-category-emails', boardId, categoryFilter],
    enabled: !!categoryFilter && !!listInboxEmails,
    staleTime: 30_000,
    queryFn: async () => {
      const rows = await listInboxEmails!(categoryFilter!)
      return new Set(rows.map((r) => String(r.id)))
    },
  })

  const categoryEmailIds =
    filters.category
      ? categoryQ.data ?? (categoryQ.isError ? new Set<string>() : null)
      : undefined

  const filteredCards = useMemo(() => {
    if (!enabled) return cards
    return applyInboxLaneFilters(cards, filters, {
      unseenByCard,
      categoryEmailIds:
        filters.category
          ? (categoryEmailIds ?? null)
          : undefined,
    })
  }, [enabled, cards, filters, unseenByCard, categoryEmailIds])

  const hubUrl = useMemo(() => {
    if (!enabled || !buildInboxListUrl) return null
    return buildInboxListUrl(inboxLaneFilterQuery(filters))
  }, [enabled, buildInboxListUrl, filters])

  return {
    enabled,
    filters,
    onChange,
    onClear,
    filteredCards,
    hubUrl,
    categoryLoading: !!filters.category && categoryQ.isFetching,
    categoryError:
      filters.category && categoryQ.isError
        ? 'Could not load category matches — showing none until retry.'
        : filters.category && !listInboxEmails
          ? 'Category filter needs the email adapter (reload or open Email Hub).'
          : null,
  }
}
